import React, { useState } from 'react';
import { useDropzone } from 'react-dropzone';
import { Card, CardContent, CardHeader } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { useToast } from '../components/ui/use-toast';
import { jobsApi, templatesApi, settingsApi, type Template, type ModelOption } from '../services/api';

type UploadType = 'file' | 'transcript';

const HomePage: React.FC = () => {
  const [uploadType, setUploadType] = useState<UploadType>('file');
  const [file, setFile] = useState<File | null>(null);
  const [transcript, setTranscript] = useState('');
  const [isUploading, setIsUploading] = useState(false);
  const [templates, setTemplates] = useState<Template[]>([]);
  const [models, setModels] = useState<ModelOption[]>([]);
  const [formData, setFormData] = useState({
    meeting_title: '',
    meeting_date: '',
    attendees: '',
    context: '',
    template_id: undefined as number | undefined,
    model: '',
  });

  const { toast } = useToast();

  // Load templates on component mount
  React.useEffect(() => {
    const load = async () => {
      try {
        const [templateList, modelList] = await Promise.all([
          templatesApi.getTemplates(),
          settingsApi.getModels(),
        ]);
        setTemplates(templateList);
        setModels(modelList || []);
      } catch (error) {
        console.error('Failed to load templates or models:', error);
      }
    };
    load();
  }, []);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    accept: {
      'audio/*': ['.mp3', '.wav', '.m4a'],
      'video/*': ['.mp4', '.mov', '.avi', '.webm']
    },
    maxFiles: 1,
    onDrop: (acceptedFiles) => {
      if (acceptedFiles.length > 0) {
        setFile(acceptedFiles[0]);
      }
    }
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (uploadType === 'file' && !file) return;
    if (uploadType === 'transcript' && !transcript.trim()) return;

    setIsUploading(true);
    try {
      let job;
      if (uploadType === 'file' && file) {
        job = await jobsApi.uploadFile(file, formData);
      } else if (uploadType === 'transcript') {
        job = await jobsApi.uploadTranscript(transcript, formData);
      }

      if (job) {
        toast({ title: 'Job created', description: `ID: ${job.id}` });
      }
      
      // Reset form
      setFile(null);
      setTranscript('');
      setFormData({
        meeting_title: '',
        meeting_date: '',
        attendees: '',
        context: '',
        template_id: undefined,
        model: '',
      });
    } catch (error) {
      console.error('Submission failed:', error);
      toast({ variant: 'destructive', title: 'Submission failed', description: 'Please try again.' });
    } finally {
      setIsUploading(false);
    }
  };

  return (
    <div className="max-w-4xl mx-auto space-y-8">
      <div className="text-center">
        <h1 className="text-4xl font-bold tracking-tight">Transform Your Meetings</h1>
        <p className="text-xl text-muted-foreground mt-4">
          Upload recordings or paste transcripts to get structured, AI-powered notes.
        </p>
      </div>

      <Card>
        <CardHeader>
          <div className="flex border-b">
            <button
              className={`px-4 py-2 text-sm font-medium ${uploadType === 'file' ? 'border-b-2 border-primary text-primary' : 'text-muted-foreground'}`}
              onClick={() => setUploadType('file')}
            >
              Upload File
            </button>
            <button
              className={`px-4 py-2 text-sm font-medium ${uploadType === 'transcript' ? 'border-b-2 border-primary text-primary' : 'text-muted-foreground'}`}
              onClick={() => setUploadType('transcript')}
            >
              Paste Transcript
            </button>
          </div>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-6 pt-4">
            {uploadType === 'file' && (
              <div>
                <label className="block text-sm font-medium mb-2">Recording File</label>
                <div
                  {...getRootProps()}
                  className={`border-2 border-dashed rounded-lg p-8 text-center cursor-pointer transition-colors ${
                    isDragActive
                      ? 'border-primary bg-primary/5'
                      : 'border-muted-foreground/25 hover:border-primary/50'
                  }`}
                >
                  <input {...getInputProps()} />
                  {file ? (
                    <div>
                      <p className="text-sm font-medium">{file.name}</p>
                      <p className="text-xs text-muted-foreground">
                        {(file.size / 1024 / 1024).toFixed(2)} MB
                      </p>
                    </div>
                  ) : (
                    <div>
                      <p className="text-sm">
                        {isDragActive
                          ? 'Drop the file here...'
                          : 'Drag & drop a file here, or click to select'}
                      </p>
                      <p className="text-xs text-muted-foreground mt-1">
                        Supports MP3, WAV, M4A, MP4, MOV, AVI, WebM
                      </p>
                    </div>
                  )}
                </div>
              </div>
            )}

            {uploadType === 'transcript' && (
              <div>
                <label className="block text-sm font-medium mb-2">Meeting Transcript</label>
                <textarea
                  value={transcript}
                  onChange={(e) => setTranscript(e.target.value)}
                  className="w-full px-3 py-2 border border-input rounded-md focus:outline-none focus:ring-2 focus:ring-ring"
                  rows={10}
                  placeholder="Paste the full transcript of your meeting here..."
                />
              </div>
            )}

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium mb-2">Meeting Title</label>
                <input
                  type="text"
                  value={formData.meeting_title}
                  onChange={(e) => setFormData({ ...formData, meeting_title: e.target.value })}
                  className="w-full px-3 py-2 border border-input rounded-md focus:outline-none focus:ring-2 focus:ring-ring"
                  placeholder="Weekly Team Standup"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">Meeting Date</label>
                <input
                  type="date"
                  value={formData.meeting_date}
                  onChange={(e) => setFormData({ ...formData, meeting_date: e.target.value })}
                  className="w-full px-3 py-2 border border-input rounded-md focus:outline-none focus:ring-2 focus:ring-ring"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium mb-2">Attendees</label>
              <input
                type="text"
                value={formData.attendees}
                onChange={(e) => setFormData({ ...formData, attendees: e.target.value })}
                className="w-full px-3 py-2 border border-input rounded-md focus:outline-none focus:ring-2 focus:ring-ring"
                placeholder="John Smith, Jane Doe, Mike Johnson"
              />
              <p className="text-xs text-muted-foreground mt-1">
                Comma-separated list of attendee names for speaker attribution
              </p>
            </div>

            <div>
              <label className="block text-sm font-medium mb-2">Meeting Context</label>
              <textarea
                value={formData.context}
                onChange={(e) => setFormData({ ...formData, context: e.target.value })}
                className="w-full px-3 py-2 border border-input rounded-md focus:outline-none focus:ring-2 focus:ring-ring"
                rows={3}
                placeholder="Brief description of the meeting purpose, agenda, or any relevant context..."
              />
            </div>

            <div>
              <label className="block text-sm font-medium mb-2">Template</label>
              <select
                value={formData.template_id || ''}
                onChange={(e) => setFormData({ 
                  ...formData, 
                  template_id: e.target.value ? parseInt(e.target.value) : undefined 
                })}
                className="w-full px-3 py-2 border border-input rounded-md focus:outline-none focus:ring-2 focus:ring-ring"
              >
                {(() => {
                  const defaultTemplate = templates.find(t => t.is_default);
                  return (
                    <option value="">
                      {defaultTemplate ? defaultTemplate.name : 'None'}
                    </option>
                  );
                })()}
                {templates
                  .filter(template => !template.is_default)
                  .map((template) => (
                    <option key={template.id} value={template.id}>
                      {template.name}
                    </option>
                  ))}
              </select>
            </div>

            <details className="rounded-md border">
              <summary className="cursor-pointer select-none px-4 py-2 text-sm font-medium">
                Advanced
              </summary>
              <div className="px-4 pb-4 pt-2">
                <label className="block text-sm font-medium mb-2">Model Override (Optional)</label>
                <select
                  value={formData.model || ''}
                  onChange={(e) => setFormData({ ...formData, model: e.target.value })}
                  className="w-full px-3 py-2 border border-input rounded-md focus:outline-none focus:ring-2 focus:ring-ring"
                >
                  <option value="">Use default from settings</option>
                  {models.map((m) => (
                    <option key={m.value} value={m.value}>
                      {m.label} — {m.description}
                    </option>
                  ))}
                </select>
                <p className="text-xs text-muted-foreground mt-1">
                  If left blank, the default model from settings will be used.
                </p>
              </div>
            </details>

            <Button
              type="submit"
              disabled={
                isUploading ||
                (uploadType === 'file' && !file) ||
                (uploadType === 'transcript' && !transcript.trim())
              }
              className="w-full"
            >
              {isUploading ? 'Uploading...' : 'Generate Notes'}
            </Button>
          </form>
        </CardContent>
      </Card>
    </div>
  );
};

export default HomePage;
